import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { RequestOption } from '@/types/ticket'

const REQUEST_SOURCE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'walk-in', label: 'Walk-in' },
  { value: 'chat', label: 'Chat' },
  { value: 'other', label: 'Other' },
]

interface ClientOption {
  id: number
  name?: string
  code?: string
}

interface ServiceTypeOption {
  id: number
  name?: string
  parent_id?: number | null
}

interface CategoryOption {
  id: number
  name?: string
}

export interface CreateTicketRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated: (request: RequestOption) => void
}

function toRequestOption(req: Record<string, unknown>): RequestOption {
  const categories: string[] = req.category ? [String(req.category)] : []
  return {
    id: (req.request_number as string) || (req.id != null ? String(req.id) : ''),
    clientName: (req.contact_name as string) || '',
    clientOrg: (req.client_name as string) || undefined,
    requestedAt: (req.submitted_at as string) || (req.created_at as string) || new Date().toISOString(),
    issueDescription: req.description as string | undefined,
    categories: categories.length > 0 ? categories : undefined,
    description: req.description as string | undefined,
    requestedQuantity: req.requested_quantity as number | undefined,
    preferredScheduleAt: req.preferred_schedule_at as string | undefined,
    attachmentMetadata: req.attachment_metadata as RequestOption['attachmentMetadata'],
    contactNumber: req.contact_number as string | undefined,
    contactName: req.contact_name as string | undefined,
    contactEmail: req.contact_email as string | undefined,
    preferredContactAt: req.preferred_contact_at as string | undefined,
    serviceTypeId: req.service_type_id as number | undefined,
    serviceType: req.service_type as string | undefined,
    serviceParent: req.service_parent as string | undefined,
    ticketStatus: req.ticket_status as number | undefined,
    ticketStatusLabel: req.ticket_status_label as string | undefined,
    requestSource: req.request_source as string | undefined,
    requestChannel: req.request_channel as string | undefined,
    submittedAt: req.submitted_at as string | undefined,
    createdAt: req.created_at as string | undefined,
    updatedAt: req.updated_at as string | undefined,
    clientId: req.client_id as number | undefined,
    clientCode: req.client_code as string | undefined,
  }
}

/** Format datetime-local value to backend "Y-m-d H:i:s" */
function toBackendDateTime(value: string): string | null {
  if (!value || value.length < 16) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

export function CreateTicketRequestModal({
  isOpen,
  onClose,
  onCreated,
}: CreateTicketRequestModalProps) {
  const { showSuccess, showError } = useToastNotification()
  const [clients, setClients] = useState<ClientOption[]>([])
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeOption[]>([])
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([])
  const [clientId, setClientId] = useState<string>('')
  const [serviceParent, setServiceParent] = useState<string>('')
  const [serviceTypeId, setServiceTypeId] = useState<string>('')
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [description, setDescription] = useState('')
  const [requestedQuantity, setRequestedQuantity] = useState(1)
  const [contactName, setContactName] = useState('')
  const [contactNumber, setContactNumber] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [preferredScheduleAt, setPreferredScheduleAt] = useState('')
  const [preferredContactAt, setPreferredContactAt] = useState('')
  const [requestSource, setRequestSource] = useState('email')
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)

  const clientCode = useMemo(() => {
    const c = clients.find((x) => String(x.id) === clientId)
    return c?.code ?? ''
  }, [clients, clientId])

  // Load clients on open
  useEffect(() => {
    if (!isOpen) return
    apiClient
      .get('/options/clients')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : []
        setClients(list)
      })
      .catch(() => setClients([]))
  }, [isOpen])

  // When client changes, load service types and categories with X-Client-Code
  useEffect(() => {
    if (!isOpen || !clientCode) {
      setServiceTypes([])
      setCategoryOptions([])
      setServiceParent('')
      setServiceTypeId('')
      setCategoryIds([])
      return
    }
    const headers = { 'X-Client-Code': clientCode }
    setIsLoadingOptions(true)
    Promise.all([
      apiClient.get('/options/service-types', { headers }),
      apiClient.get('/support/my-request/categories', { headers }),
    ])
      .then(([typesRes, catRes]) => {
        const types = Array.isArray(typesRes.data) ? typesRes.data : []
        setServiceTypes(types)
        const catList = catRes.data?.data ?? catRes.data ?? []
        setCategoryOptions(Array.isArray(catList) ? catList : [])
      })
      .catch(() => {
        setServiceTypes([])
        setCategoryOptions([])
      })
      .finally(() => setIsLoadingOptions(false))
  }, [isOpen, clientCode])

  const parentOptions = useMemo(() => {
    return serviceTypes
      .filter((s) => !s.parent_id)
      .map((s) => ({ value: String(s.id), label: s.name || String(s.id) }))
  }, [serviceTypes])

  const childOptions = useMemo(() => {
    if (!serviceParent) return []
    return serviceTypes
      .filter((s) => s.parent_id != null && String(s.parent_id) === serviceParent)
      .map((s) => ({ value: String(s.id), label: s.name || String(s.id) }))
  }, [serviceTypes, serviceParent])

  const hasChildTypes = childOptions.length > 0
  const effectiveServiceId = hasChildTypes ? serviceTypeId : serviceParent

  useEffect(() => {
    if (serviceParent) setServiceTypeId('')
  }, [serviceParent])

  const errors = useMemo(() => {
    const e: Record<string, string> = {}
    if (!clientId) e.client = 'Client is required'
    if (!serviceParent) e.serviceParent = 'Service is required'
    if (hasChildTypes && !serviceTypeId) e.serviceType = 'Type of Service is required'
    if (!description.trim()) e.description = 'Description is required'
    if (!contactName.trim()) e.contactName = 'Contact Name is required'
    if (!contactNumber.trim()) e.contactNumber = 'Contact Number is required'
    if (!contactEmail.trim()) e.contactEmail = 'Contact Email is required'
    else if (!/^\S+@\S+\.\S+$/.test(contactEmail)) e.contactEmail = 'Enter a valid email address'
    if (!requestedQuantity || requestedQuantity < 1) e.requestedQuantity = 'Must be at least 1'
    return e
  }, [
    clientId,
    serviceParent,
    hasChildTypes,
    serviceTypeId,
    description,
    contactName,
    contactNumber,
    contactEmail,
    requestedQuantity,
  ])

  const hasErrors = Object.keys(errors).length > 0

  const resetForm = () => {
    setClientId('')
    setServiceParent('')
    setServiceTypeId('')
    setCategoryIds([])
    setDescription('')
    setRequestedQuantity(1)
    setContactName('')
    setContactNumber('')
    setContactEmail('')
    setPreferredScheduleAt('')
    setPreferredContactAt('')
    setRequestSource('email')
    setTouched({})
  }

  const handleClose = () => {
    onClose()
    resetForm()
  }

  const handleSubmit = async () => {
    setTouched({
      client: true,
      serviceParent: true,
      serviceType: true,
      description: true,
      contactName: true,
      contactNumber: true,
      contactEmail: true,
      requestedQuantity: true,
    })
    if (hasErrors || !clientCode) {
      if (!clientCode && clientId) showError('Please select a client with a code.')
      return
    }

    setIsSubmitting(true)
    try {
      const payload = {
        service_type_id: Number(effectiveServiceId),
        category_ids: categoryIds.map(Number).filter(Boolean),
        description: description.trim(),
        requested_quantity: requestedQuantity,
        preferred_schedule_at: toBackendDateTime(preferredScheduleAt),
        contact_number: contactNumber.trim(),
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim(),
        preferred_contact_at: toBackendDateTime(preferredContactAt),
        request_source: requestSource,
      }

      const res = await apiClient.post('/support/my-request/admin', payload, {
        headers: { 'X-Client-Code': clientCode },
      })

      const data = res.data?.data ?? res.data
      const request = toRequestOption(data as Record<string, unknown>)
      showSuccess('Ticket request created successfully.')
      handleClose()
      onCreated(request)
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
      const message = ax.response?.data?.message ?? 'Failed to create ticket request'
      const errs = ax.response?.data?.errors
      showError(errs ? Object.values(errs).flat().join(' ') : message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh" display="flex" flexDirection="column">
        <ModalHeader>Add Ticket Request</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" flex="1" minH={0}>
          <VStack align="stretch" spacing={4}>
            <FormControl isInvalid={!!errors.client && touched.client} isRequired>
              <FormLabel>Client</FormLabel>
              <Select
                placeholder="Select client"
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value)
                  setTouched((p) => ({ ...p, client: true }))
                }}
              >
                {clients.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name || c.code || c.id}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.client}</FormErrorMessage>
            </FormControl>

            {clientCode && (
              <>
                <FormControl
                  isInvalid={!!errors.serviceParent && touched.serviceParent}
                  isRequired
                  isDisabled={isLoadingOptions}
                >
                  <FormLabel>Service</FormLabel>
                  <Select
                    placeholder="Select service"
                    value={serviceParent}
                    onChange={(e) => {
                      setServiceParent(e.target.value)
                      setTouched((p) => ({ ...p, serviceParent: true }))
                    }}
                  >
                    {parentOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.serviceParent}</FormErrorMessage>
                </FormControl>

                {hasChildTypes && (
                  <FormControl
                    isInvalid={!!errors.serviceType && touched.serviceType}
                    isRequired
                  >
                    <FormLabel>Type of Service</FormLabel>
                    <Select
                      placeholder="Select type"
                      value={serviceTypeId}
                      onChange={(e) => {
                        setServiceTypeId(e.target.value)
                        setTouched((p) => ({ ...p, serviceType: true }))
                      }}
                    >
                      {childOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.serviceType}</FormErrorMessage>
                  </FormControl>
                )}

                {categoryOptions.length > 0 && (
                  <FormControl>
                    <FormLabel>Categories</FormLabel>
                    <CheckboxGroup value={categoryIds} onChange={(v) => setCategoryIds((v as string[]) || [])}>
                      <Wrap spacing={2}>
                        {categoryOptions.map((c) => (
                          <WrapItem key={c.id}>
                            <Checkbox value={String(c.id)} size="sm">
                              {c.name || c.id}
                            </Checkbox>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </CheckboxGroup>
                  </FormControl>
                )}

                <FormControl isInvalid={!!errors.description && touched.description} isRequired>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={() => setTouched((p) => ({ ...p, description: true }))}
                    placeholder="Issue description"
                    minH="80px"
                  />
                  <FormErrorMessage>{errors.description}</FormErrorMessage>
                </FormControl>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl isInvalid={!!errors.requestedQuantity && touched.requestedQuantity} isRequired>
                    <FormLabel>Devices Affected</FormLabel>
                    <NumberInput
                      value={requestedQuantity}
                      min={1}
                      onChange={(_, n) =>
                        setRequestedQuantity(Number.isNaN(n) ? 1 : n)
                      }
                      onBlur={() => setTouched((p) => ({ ...p, requestedQuantity: true }))}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormErrorMessage>{errors.requestedQuantity}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Request Source</FormLabel>
                    <Select
                      value={requestSource}
                      onChange={(e) => setRequestSource(e.target.value)}
                    >
                      {REQUEST_SOURCE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl isInvalid={!!errors.contactName && touched.contactName} isRequired>
                    <FormLabel>Contact Name</FormLabel>
                    <Input
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      onBlur={() => setTouched((p) => ({ ...p, contactName: true }))}
                      placeholder="Contact person"
                    />
                    <FormErrorMessage>{errors.contactName}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.contactNumber && touched.contactNumber} isRequired>
                    <FormLabel>Contact Number</FormLabel>
                    <Input
                      value={contactNumber}
                      onChange={(e) => setContactNumber(e.target.value)}
                      onBlur={() => setTouched((p) => ({ ...p, contactNumber: true }))}
                      placeholder="Phone number"
                    />
                    <FormErrorMessage>{errors.contactNumber}</FormErrorMessage>
                  </FormControl>
                </Grid>

                <FormControl isInvalid={!!errors.contactEmail && touched.contactEmail} isRequired>
                  <FormLabel>Contact Email</FormLabel>
                  <Input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    onBlur={() => setTouched((p) => ({ ...p, contactEmail: true }))}
                    placeholder="email@example.com"
                  />
                  <FormErrorMessage>{errors.contactEmail}</FormErrorMessage>
                </FormControl>

                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <FormControl>
                    <FormLabel>Preferred Schedule</FormLabel>
                    <Input
                      type="datetime-local"
                      value={preferredScheduleAt}
                      onChange={(e) => setPreferredScheduleAt(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Preferred Contact Date & Time</FormLabel>
                    <Input
                      type="datetime-local"
                      value={preferredContactAt}
                      onChange={(e) => setPreferredContactAt(e.target.value)}
                    />
                  </FormControl>
                </Grid>
              </>
            )}

            {!clientId && (
              <Text fontSize="sm" color="gray.500">
                Select a client to load service types and categories.
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={hasErrors || !clientCode}
          >
            Create Request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default CreateTicketRequestModal
